import axios, { AxiosInstance } from 'axios';
import baseAxios from '@/helpers/forms/axios';

export interface AttachmentUpload {
    uuid: string
    url: string
    fields: Record<string, string>
    maxSizeBytes: number
    expiresAt: string
}

interface AttachmentUploadResponse {
    data: {
        uuid: string
        url: string
        fields: Record<string, string>
        max_size_bytes: number
        expires_at: string
    }
}

const storageClient = axios.create({ withCredentials: false, withXSRFToken: false });

export default (client: AxiosInstance = baseAxios): {
    requestUpload: (name: string) => Promise<AttachmentUpload>
    uploadToStorage: (upload: AttachmentUpload, file: File) => Promise<void>
    attachUpload: (shipmentType: 'inbound' | 'outbound', shipmentId: number, uuid: string) => Promise<void>
    deleteAttachment: (attachmentId: number) => Promise<void>
} => ({
    requestUpload: async (name: string): Promise<AttachmentUpload> => {
        const response = await client.post<AttachmentUploadResponse>('/api/attachment-uploads', { name }, {
            // Signed storage fields must bypass the API client's case conversion.
            transformResponse: axios.defaults.transformResponse,
        });
        const upload = response.data.data;

        return {
            uuid: upload.uuid,
            url: upload.url,
            fields: upload.fields,
            maxSizeBytes: upload.max_size_bytes,
            expiresAt: upload.expires_at,
        };
    },
    uploadToStorage: async (upload: AttachmentUpload, file: File): Promise<void> => {
        const data = new FormData();
        Object.entries(upload.fields).forEach(([key, value]) => data.append(key, value));
        data.append('file', file);

        await storageClient.post(upload.url, data);
    },
    attachUpload: async (shipmentType: 'inbound' | 'outbound', shipmentId: number, uuid: string): Promise<void> => {
        await client.post(`/api/shipments/${shipmentType}/${shipmentId}/attachment-uploads/${uuid}`);
    },
    deleteAttachment: async (attachmentId: number): Promise<void> => {
        return await client.delete(`/api/attachments/${attachmentId}`);
    },
});
